import React, { useContext, useState, useEffect } from 'react';
import { ChevronLeft, AlertCircle, Loader2, Building2, History, Calendar, User, Filter, X, Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../context/AuthContext';
import subscriptionServices from '../../../../services/subscriptionServices';
import superAdminServices from '../../../../services/superAdminServices';
import Pagination from '../../../../components/Pagination';

export default function SubscriptionHistory() {
  const navigate = useNavigate();
  const { id } = useParams(); // Organization ID from URL
  const { user, authTokens } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [organization, setOrganization] = useState(null);
  const [history, setHistory] = useState([]);
  const [showFiltersSection, setShowFiltersSection] = useState(false);

  const [filters, setFilters] = useState({
    action: '',
    searchTerm: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });

  useEffect(() => {
    if (id) {
      fetchOrganizationInfo();
      fetchSubscriptionHistory();
    }
  }, [id, pagination.page, pagination.page_size]);

  const fetchOrganizationInfo = async () => {
    try {
      const response = await superAdminServices.getOrganizationDetails(authTokens.access, id);
      if (response.organization) {
        setOrganization(response.organization);
      }
    } catch (err) {
      console.error('Error fetching organization info:', err);
    }
  };

  const fetchSubscriptionHistory = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        organization_id: id,
        page: pagination.page,
        page_size: pagination.page_size
      };

      if (filters.action) {
        params.action = filters.action;
      }

      const response = await subscriptionServices.getSubscriptionHistory(authTokens.access, params);
      
      if (response.success) {
        setHistory(response.data.results || []);
        setPagination(prev => ({
          ...prev,
          total_pages: response.data.total_pages || 0,
          count: response.data.count || 0
        }));
      } else {
        setError(response.error || 'Erreur lors du chargement de l\'historique');
      }
    } catch (err) {
      setError('Erreur de connexion lors du chargement de l\'historique');
      console.error('Error fetching subscription history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin-panel/organizations');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSubscriptionHistory();
  };

  const handleResetFilters = () => {
    setFilters({
      action: '',
      searchTerm: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => {
      fetchSubscriptionHistory();
    }, 100);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }) + ' à ' + date.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const actionConfig = {
      subscribe: { bg: 'bg-green-100', text: 'text-green-700', label: 'Abonnement' },
      unsubscribe: { bg: 'bg-red-100', text: 'text-red-700', label: 'Désabonnement' },
      renew: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Renouvellement' },
      suspend: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Suspension' },
      reactivate: { bg: 'bg-green-100', text: 'text-green-700', label: 'Réactivation' },
      update: { bg: 'bg-green-100', text: 'text-green-700', label: 'Modification' }
    };

    const config = actionConfig[action] || { bg: 'bg-gray-100', text: 'text-gray-700', label: action };
    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading && !history.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Chargement de l'historique...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-3 p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Historique des abonnements</h1>
            <p className="text-sm text-gray-400 mt-1">
              {organization ? `Historique pour ${organization.organisation_name}` : 'Chargement...'}
            </p>
          </div>
        </div>

        <button
          onClick={handleShowFilters}
          className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>Filtres</span>
        </button>
      </div>

      {/* Organization Info */}
      {organization && (
        <div className="bg-blue-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Building2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-800">{organization.organisation_name}</h3>
              <p className="text-xs text-green-600 mt-1">{organization.organisation_description || 'Aucune description'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Erreur</h3>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className='flex items-center justify-between'>
            <div className="flex items-center mb-4">
              <Filter className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Filtres</h3>
            </div>
            <button onClick={handleShowFilters} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Action filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Type d'action
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Toutes les actions</option>
                <option value="subscribe">Abonnement</option>
                <option value="unsubscribe">Désabonnement</option>
                <option value="renew">Renouvellement</option>
                <option value="suspend">Suspension</option>
                <option value="reactivate">Réactivation</option>
                <option value="update">Modification</option>
              </select>
            </div>

            {/* Search filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Raison, utilisateur..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                  className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Filter action buttons */}
          <div className="flex items-center space-x-3 justify-end">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* History Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Évolution de l'abonnement</h2>
          <div className="flex items-center text-gray-500">
            <History className="h-5 w-5 mr-2" />
            <span>{pagination.count} entrées d'historique</span>
          </div>
        </div>

        {loading && history.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
            <span className="text-gray-600">Chargement de l'historique...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-12">
            <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Aucun historique trouvé</p>
            <p className="text-gray-500">Cette organisation n'a pas encore d'historique d'abonnement.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {history.map((entry, index) => (
              <div key={entry.id || index} className="border border-gray-200 rounded-lg p-6 relative">
                {/* Timeline connector */}
                {index < history.length - 1 && (
                  <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-300"></div>
                )}
                
                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    entry.action === 'subscribe' ? 'bg-green-100 text-green-600' :
                    entry.action === 'unsubscribe' ? 'bg-red-100 text-red-600' :
                    entry.action === 'renew' ? 'bg-blue-100 text-blue-600' :
                    entry.action === 'suspend' ? 'bg-yellow-100 text-yellow-600' :
                    entry.action === 'reactivate' ? 'bg-green-100 text-green-600' :
                    entry.action === 'update' ? 'bg-purple-100 text-purple-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    <History className="h-4 w-4" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      {getActionBadge(entry.action)}
                      
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          <span>{entry.performed_by || 'Système'}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{formatDate(entry.action_date)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {entry.reason && (
                      <div className="mb-3">
                        <p className="text-gray-700 font-medium">{entry.reason}</p>
                      </div>
                    )}
                    
                    {(entry.subscription_start_date || entry.subscription_end_date) && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-800 mb-2">Détails de la période:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {entry.subscription_start_date && (
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-600">Début:</span>
                              <span className="text-gray-900 font-medium">
                                {new Date(entry.subscription_start_date).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          )}
                          {entry.subscription_end_date && (
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-600">Fin:</span>
                              <span className="text-gray-900 font-medium">
                                {new Date(entry.subscription_end_date).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {entry.notes && (
                      <div className="mt-3 text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {entry.notes}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {history.length > 0 && (
          <div className="mt-6">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.total_pages}
              totalItems={pagination.count}
              pageSize={pagination.page_size}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={loading}
              itemName="entrées d'historique"
            />
          </div>
        )}
      </div>
    </div>
  );
}
